import type { RandomPercentageCondition as RandomPercentageConditionType } from "@redotech/redo-flows-common/rules-v3/schemas";
import { RuleConditionType } from "@redotech/redo-flows-common/rules/types";
import { useTranslation } from "@redotech/redo-i18n";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo } from "react";

const canEdit = (
  editableFields: ReadonlySet<string> | undefined,
  field: string,
) => editableFields === undefined || editableFields.has(field);

export const RandomPercentageCondition = memo(
  function RandomPercentageCondition({
    condition,
    onChange,
    editableFields,
  }: {
    condition: RandomPercentageConditionType;
    onChange: (updated: RandomPercentageConditionType) => void;
    editableFields?: ReadonlySet<string>;
  }) {
    const { t } = useTranslation();
    const percentageEditable = canEdit(editableFields, "percentage");

    return (
      <Flex dir="column" gap="md">
        <Text fontSize="sm" textColor="secondary">
          {t(
            "Passes randomly for the given percentage of tickets. For example, 50% means this condition is true for approximately half of all tickets.",
          )}
        </Text>
        <Flex align="center" gap="sm">
          <RedoTextInput
            inputTypeHint="number"
            max={100}
            min={0}
            placeholder="50"
            setValue={(value: string) => {
              if (!percentageEditable) {
                return;
              }
              const num = parseFloat(value);
              onChange({
                ...condition,
                percentage: isNaN(num) ? 50 : Math.min(100, Math.max(0, num)),
              });
            }}
            size="sm"
            state={percentageEditable ? "default" : "disabled"}
            step={1}
            value={String(condition.percentage ?? 50)}
          />
          <Text fontSize="sm" textColor="secondary">
            %
          </Text>
        </Flex>
      </Flex>
    );
  },
);

export function buildDefaultRandomPercentageCondition(): RandomPercentageConditionType {
  return { conditionType: RuleConditionType.RANDOM_PERCENTAGE, percentage: 50 };
}
